// Read-only view of the chain. Nothing here needs a wallet.
import { GnoJSONRPCProvider } from '@gnolang/gno-js-client';
import { CONFIG } from '../config.js';
import { gnoValue, gnoValues } from './gnolit.js';
import { pageWindow } from './board.js';

let providerPromise;
export function provider() {
  // GnoJSONRPCProvider's constructor takes no URL; `new` fails deep inside a
  // dependency. The async factory is the only way in.
  providerPromise ??= GnoJSONRPCProvider.create(CONFIG.rpc);
  return providerPromise;
}

const isNotFound = (e) => /package not found|InvalidPkgPath/i.test(String(e?.message ?? e)) || e?.constructor?.name === 'InvalidPkgPathError';

export async function chainStatus() {
  const p = await provider();
  const [height, status] = await Promise.all([p.getBlockNumber(), p.getStatus()]);
  return { height, chainId: status.node_info.network };
}

export async function isDeployed(pkgPath) {
  const p = await provider();
  try { await p.getFunctionSignatures(pkgPath); return true; }
  catch (e) { if (isNotFound(e)) return false; throw e; }
}

// Does the realm AT pkgPath export a function named funcName RIGHT NOW? Same
// vm/qfuncs query isDeployed runs above -- CONFIG.duelRealm (or any pkgPath)
// names a location, not a promise that whatever is deployed there still
// speaks the ABI a caller is about to send. Errors are NOT swallowed here
// (unlike isDeployed's "not found" -> false): chain/compat.js needs to tell
// "asked, and the function is missing" apart from "could not ask at all",
// and folding the second into a bare `false` would erase that difference.
export async function hasFunction(pkgPath, funcName) {
  const p = await provider();
  const sigs = await p.getFunctionSignatures(pkgPath);
  return sigs.some((s) => s.FuncName === funcName);
}

// --- wugnot ---------------------------------------------------------------

export async function wugnotBalance(addr) {
  const p = await provider();
  return gnoValue(await p.evaluateExpression(CONFIG.wugnotRealm, `BalanceOf("${addr}")`));
}

export async function wugnotAllowance(owner, spender) {
  const p = await provider();
  return gnoValue(await p.evaluateExpression(CONFIG.wugnotRealm, `Allowance("${owner}", "${spender}")`));
}

export async function ugnotBalance(addr) {
  const p = await provider();
  return BigInt(await p.getBalance(addr, 'ugnot'));
}

// --- the duel realm ---------------------------------------------------------
// Every read below maps 1:1 onto an exported function of the duel v3 realm
// (CONFIG.duelRealm). REDESIGNED 2026-10-08: no secret, no nonce, no
// commit/reveal, no reveal deadline, no forfeit. Post/Accept/Resolve are the
// only writes with money behind them; everything here is read-only.

export async function ladder() {
  const p = await provider();
  const n = Number(gnoValue(await p.evaluateExpression(CONFIG.duelRealm, 'RungCount()')));
  const stakes = [];
  for (let i = 0; i < n; i++) stakes.push(gnoValue(await p.evaluateExpression(CONFIG.duelRealm, `StakeOfRung(${i})`)));
  return stakes;
}

export async function escrowed() {
  const p = await provider();
  return gnoValue(await p.evaluateExpression(CONFIG.duelRealm, 'Escrowed()'));
}

// The listers: how many, and which IDs, ascending. The board is composed
// from these in board.js; Render("") is not read anywhere.
export async function offerCount() {
  const p = await provider();
  return Number(gnoValue(await p.evaluateExpression(CONFIG.duelRealm, 'OfferCount()')));
}

export async function duelCount() {
  const p = await provider();
  return Number(gnoValue(await p.evaluateExpression(CONFIG.duelRealm, 'DuelCount()')));
}

export async function offerIds(offset, limit) {
  const p = await provider();
  const w = pageWindow(offset, limit);
  return gnoValue(await p.evaluateExpression(CONFIG.duelRealm, `OfferIDs(${w.offset}, ${w.limit})`));
}

export async function duelIds(offset, limit) {
  const p = await provider();
  const w = pageWindow(offset, limit);
  return gnoValue(await p.evaluateExpression(CONFIG.duelRealm, `DuelIDs(${w.offset}, ${w.limit})`));
}

// An offer standing in the lobby: OfferOf (poster, rung, postedAt) plus
// OfferChallenge (opponent, label), kept as two calls the same way the old
// realm split money from the challenge fields.
//
// A non-existent offer is told apart by its zero poster, checked first.
// THERE IS NO COMMITMENT ANY MORE -- the old commitHash field (and its ""
// legacy marker) is simply gone from this record, because Post no longer
// takes one.
export async function offerOf(id) {
  const p = await provider();
  const [poster, rung, postedAt] = gnoValues(await p.evaluateExpression(CONFIG.duelRealm, `OfferOf(${id})`));
  if (!poster) return null;
  const [opponent, label] = gnoValues(await p.evaluateExpression(CONFIG.duelRealm, `OfferChallenge(${id})`));
  return { id: BigInt(id), poster, rung: Number(rung), postedAt, opponent, label };
}

// A duel still in flight. ONLY IN-FLIGHT DUELS EXIST HERE: Resolve pays out
// AND DELETES the record in the same call, so DuelOf answering at all means
// this duel has not settled yet -- there is no `resolved` flag to check,
// because an answer IS the "not resolved" answer. A settled duel's only
// trace on chain from here on is RecentResults (below) or the event log.
//
// round IS THE PINNED BEACON ROUND duel.gno fixes at Accept time -- THE MOST
// IMPORTANT NUMBER ON THIS SURFACE, per the brief this slice shipped against,
// and NOT yet turned into a countdown here: that needs the beacon's
// TimeOfRound/CurrentRound, which is the next slice's job, not this one. This
// read hands the round through honestly rather than pretending it is a block
// height (the OLD realm's reveal deadline) or inventing a countdown this file
// has not earned yet.
export async function duelOf(id) {
  const p = await provider();
  const [poster, taker, rung, round, acceptedAt] = gnoValues(await p.evaluateExpression(CONFIG.duelRealm, `DuelOf(${id})`));
  if (!poster) return null;
  const label = gnoValue(await p.evaluateExpression(CONFIG.duelRealm, `DuelLabel(${id})`));
  return { id: BigInt(id), poster, taker, rung: Number(rung), round, acceptedAt, label };
}

// DuelRound and DuelLabel: the same two fields DuelOf's bundle already
// carries (round, label), exported separately by the realm. duelOf above
// calls DuelLabel itself rather than this one; these two are exposed for a
// caller that wants only one of them without the rest of the record.
export async function duelRound(id) {
  const p = await provider();
  return gnoValue(await p.evaluateExpression(CONFIG.duelRealm, `DuelRound(${id})`));
}

export async function duelLabel(id) {
  const p = await provider();
  return gnoValue(await p.evaluateExpression(CONFIG.duelRealm, `DuelLabel(${id})`));
}

// --- the pending pot: rake waiting on FlushPot to reach the weekly draw ----
// Every duel's rake lands here first; FlushPot (permissionless) is what
// moves it into the raffle as entries. Nothing in this slice calls FlushPot
// or renders these -- they are wired as reads only, for a later slice.
export async function pendingPot() {
  const p = await provider();
  return gnoValue(await p.evaluateExpression(CONFIG.duelRealm, 'PendingPot()'));
}

export async function pendingEntryUnits() {
  const p = await provider();
  return gnoValue(await p.evaluateExpression(CONFIG.duelRealm, 'PendingEntryUnits()'));
}

export async function pendingEntryHolders() {
  const p = await provider();
  return gnoValue(await p.evaluateExpression(CONFIG.duelRealm, 'PendingEntryHolders()'));
}

export async function pendingSince() {
  const p = await provider();
  return gnoValue(await p.evaluateExpression(CONFIG.duelRealm, 'PendingSince()'));
}

// --- settled history: RecentResults is the ONLY record of a settled duel --
// Resolve deletes the Offer/Duel record it pays out; RecentResults is a ring
// buffer of the last ResultRingSize() (32, read live rather than hardcoded)
// settlements, MOST RECENT FIRST. Anything older than that is in the event
// log only, which this front end does not read.
export async function resultCount() {
  const p = await provider();
  return gnoValue(await p.evaluateExpression(CONFIG.duelRealm, 'ResultCount()'));
}

export async function resultsSeen() {
  const p = await provider();
  return gnoValue(await p.evaluateExpression(CONFIG.duelRealm, 'ResultsSeen()'));
}

export async function resultRingSize() {
  const p = await provider();
  return Number(gnoValue(await p.evaluateExpression(CONFIG.duelRealm, 'ResultRingSize()')));
}

// RecentResults(limit) []Result, Result{DuelID, Round, Winner, Loser, Rung,
// Height} -- a SLICE OF STRUCTS, which nothing this page read before today.
// gnolit.js's struct branch (added for exactly this call, proved live against
// onyx-1 -- see that file and its test) hands back each Result as a plain
// array in FIELD DECLARATION ORDER, because the VM's printer never names
// struct fields; the zip below is this file's one place that has to already
// know that order, from the brief this slice shipped against.
export async function recentResults(limit) {
  const p = await provider();
  const rows = gnoValue(await p.evaluateExpression(CONFIG.duelRealm, `RecentResults(${limit})`));
  return rows.map(([duelId, round, winner, loser, rung, height]) => ({
    duelId, round, winner, loser, rung: Number(rung), height,
  }));
}

// --- the beacon -------------------------------------------------------------

export async function beaconLatest() {
  const p = await provider();
  try {
    const [round, randomness] = gnoValues(await p.evaluateExpression(CONFIG.beaconRealm, 'Latest()'));
    return { round, randomness };
  } catch (e) {
    if (/no round published/i.test(String(e?.message))) return null;
    throw e;
  }
}

export async function beaconPublished(round) {
  const p = await provider();
  return gnoValue(await p.evaluateExpression(CONFIG.beaconRealm, `Published(${round})`));
}

export async function beaconRandomness(round) {
  const p = await provider();
  return gnoValue(await p.evaluateExpression(CONFIG.beaconRealm, `Randomness(${round})`));
}

// TimeOfRound/CurrentRound: the beacon's own clock, read fresh every poll
// rather than trusted from the browser's. TimeOfRound(round) is the unix
// second THAT round is signed -- the number duel-data.js turns into the
// countdown's "in 4:12", and the number that proves the fairness claim: a
// player can open drand's own site and watch that exact round not exist,
// then exist. CurrentRound() is "now", answered by the chain rather than
// this tab's clock, which is what tells the page a pinned round's time has
// actually arrived rather than guessing from a client clock that might be
// wrong.
export async function timeOfRound(round) {
  const p = await provider();
  return gnoValue(await p.evaluateExpression(CONFIG.beaconRealm, `TimeOfRound(${round})`));
}

export async function currentRound() {
  const p = await provider();
  return gnoValue(await p.evaluateExpression(CONFIG.beaconRealm, 'CurrentRound()'));
}

// The page the player can open to check the coin themselves.
export function beaconRoundUrl(round) {
  return `${CONFIG.gnoweb}/${CONFIG.beaconRealm.replace(/^gno\.land\//, '')}:${round}`;
}
export function realmUrl(pkgPath) {
  return `${CONFIG.gnoweb}/${pkgPath.replace(/^gno\.land\//, '')}`;
}

// --- the room ---------------------------------------------------------------
//
// Recent(limit) []Message, Message{ID, Author, Text, Height, Time} -- the same
// SLICE OF STRUCTS shape as RecentResults above, and the same caveat applies:
// the VM's printer never names struct fields, so gnolit.js hands back each
// Message as a plain array in FIELD DECLARATION ORDER and the zip below is the
// one place that has to know that order. If chat.gno's Message struct ever
// gains or reorders a field, THIS LINE is what breaks, silently, by shifting
// every value one place -- which is exactly how the keeper once read a duel's
// Abandoned flag as its Resolved flag. Pinned by chatShape() in the test suite.
export async function chatRecent(limit) {
  const p = await provider();
  const rows = gnoValue(await p.evaluateExpression(CONFIG.chatRealm, `Recent(${limit})`));
  return rows.map(([id, author, text, height, time]) => ({
    id, author, text, height, time: Number(time),
  }));
}

export async function chatSeen() {
  const p = await provider();
  return gnoValue(await p.evaluateExpression(CONFIG.chatRealm, 'Seen()'));
}

export async function chatMaxTextLen() {
  const p = await provider();
  return gnoValue(await p.evaluateExpression(CONFIG.chatRealm, 'MaxTextLen()'));
}

// The first height `addr` may post at again, or 0 if they never have. Read so
// the page can disable its own button instead of letting somebody pay gas to
// be refused by the cooldown.
export async function chatNextAllowedHeight(addr) {
  const p = await provider();
  return gnoValue(await p.evaluateExpression(CONFIG.chatRealm, `NextAllowedHeight("${addr}")`));
}

// --- the pin arithmetic, ASKED OF THE REALM ---------------------------------
//
// pinOffsetRounds and bucketRounds are a SECURITY BUDGET (read duel.gno's own
// comment on pinOffsetRounds). A JavaScript copy of a security budget is one
// that drifts silently the moment anyone retunes it, so the page asks rather
// than remembers. Exported by duel/v4 on 2026-10-08 for exactly this.
export async function duelRoundForAcceptAt(currentRound) {
  const p = await provider();
  return gnoValue(await p.evaluateExpression(CONFIG.duelRealm, `RoundForAcceptAt(${currentRound})`));
}

export async function duelBucketRounds() {
  const p = await provider();
  return gnoValue(await p.evaluateExpression(CONFIG.duelRealm, 'BucketRounds()'));
}
